// Devolutions RDM (Remote Desktop Manager) JSON import (spec §13). An RDM export is
// `{ Connections: [...], DatabaseID, Version }`. Each connection has a numeric ConnectionType, a
// `Group` path (`\`-delimited), a Name/ID, and a type-specific block. We map it onto our tree:
// groups → folder nodes; groups carrying an IP → device nodes; leaf connections → access items
// (ssh/rdp/website/vpn) under their group/device. The samples carry NO credentials, so accesses
// import as connection metadata (host/url) with empty username/password to fill in later.

import type { Field } from "./items";
import { parseCSV } from "./csv";
import { buildImportTree, fld, parentOf, segments, type ImportAccess, type ImportNode, type ImportTree } from "./importTree";

// Back-compat type aliases — the shared tree model now lives in importTree.ts.
export type RdmNode = ImportNode;
export type RdmAccess = ImportAccess;
export type RdmParsed = ImportTree;

interface RdmConn {
  ConnectionType?: number;
  ID?: string;
  Group?: string;
  Name?: string;
  Description?: string;
  WebBrowserUrl?: string;
  Url?: string;
  // Credential-bearing blocks. Usernames are plaintext (casing differs per block); passwords are
  // stored as RDM `SafePassword` — an AES fixed-key encrypted blob, NOT plaintext, so we do not
  // import them as-is (they would be unusable ciphertext). See parseRdm note.
  Terminal?: { Host?: string; Username?: string; SafePassword?: string };
  Web?: { UserName?: string; SafePassword?: string };
  VPN?: { UserName?: string; VPNSafePassword?: string };
  IDrac?: { Url?: string };
  ILO?: { Url?: string };
  HostDetails?: { Host?: string };
  GroupDetails?: { GroupType?: number; IP?: string };
  VMWare?: { Server?: string; Application?: number };
  DataEntry?: { Others?: { Name?: string; Order?: number }[] };
}


/** Best-effort device category from a name. */
export function inferCategory(name: string): string {
  const n = name.toLowerCase();
  if (/idrac|ipmi|ilo|\bbmc\b/.test(n)) return "BMC/iDRAC";
  if (/qnap|synology|\bnas\b|storage|ift-|truenas|\bds\d|\bgs\d/.test(n)) return "Storage";
  if (/firewall|sonicwall|fortigate|fortinet|palo\s?alto|pfsense|\btz\d/.test(n)) return "Firewall";
  if (/\bups\b|\bapc\b/.test(n)) return "UPS";
  if (/camera|kamer|hikvision|\baxis\b|dahua|\bnvr\b/.test(n)) return "Camera";
  if (/server|esxi|vmware|hyper-?v|\bvbr\b|backup|\bsrv\b/.test(n)) return "Server";
  if (/switch|\bsw\d|aruba|cisco|catalyst|juniper|mikrotik|router|\bap\d|wi-?fi|\bwlc\b|controler|controller|n1100|prtg|networking/.test(n)) return "Network";
  return "Other";
}

/** IP without any CIDR mask. */
const cleanAddr = (ip: string): string => ip.split("/")[0].trim();

/** A username field, included only when present (plaintext in RDM). */
const userField = (value?: string): Field[] => (value ? [fld("username", "Username", "username", value)] : []);

/** Map a leaf connection to an access item, or null if it is a group (handled as a node). */
function leafToAccess(c: RdmConn): Omit<RdmAccess, "groupPath"> | null {
  const name = c.Name || "(unnamed)";
  const notes = c.Description || undefined;
  switch (c.ConnectionType) {
    case 1: // RDP
      return { type: "rdp", name, notes, fields: [fld("host", "Host", "host", c.Url || ""), fld("port", "Port", "port", "3389")] };
    case 77: // SSH
      return {
        type: "ssh",
        name,
        notes,
        fields: [fld("host", "Host", "host", c.Terminal?.Host || ""), fld("port", "Port", "port", "22"), ...userField(c.Terminal?.Username)],
      };
    case 5: // Web browser
      return { type: "website", name, notes, fields: [fld("url", "URL", "url", c.WebBrowserUrl || ""), ...userField(c.Web?.UserName)] };
    case 84: // Dell iDRAC
      return { type: "website", name, notes, fields: [fld("url", "URL", "url", c.IDrac?.Url || ""), ...userField(c.Web?.UserName)] };
    case 53: // host / embedded web
      return { type: "website", name, notes, fields: [fld("url", "URL", "url", c.HostDetails?.Host || ""), ...userField(c.Web?.UserName)] };
    case 20: // VPN
      return { type: "vpn", name, notes, fields: [fld("server_address", "Server address", "host", c.Url || ""), ...userField(c.VPN?.UserName)] };
    case 13: // VMware (vCenter/ESXi web UI)
      return {
        type: "website",
        name,
        notes,
        fields: [fld("url", "URL", "url", c.VMWare?.Server ? `https://${c.VMWare.Server}/` : ""), ...userField(c.Web?.UserName)],
      };
    case 74: // web-managed appliance reached via Terminal.Host (e.g. UPS)
      return { type: "website", name, notes, fields: [fld("url", "URL", "url", c.Terminal?.Host || ""), ...userField(c.Web?.UserName)] };
    case 85: // appliance reached via ILO.Url (e.g. PRTG probe / iLO)
      return { type: "website", name, notes, fields: [fld("url", "URL", "url", c.ILO?.Url || ""), ...userField(c.Web?.UserName)] };
    case 32: {
      // RDM data-entry list (e.g. a public-IP pool). The encrypted SafeValues are unusable, but the
      // entry names (the IPs) are plaintext — keep them in notes so nothing visible is lost. Use
      // "generic" (a no-field item that renders its notes); "note" is a field type, not a catalog
      // item type, so it fell back to the generic template anyway while storing an unregistered type.
      const entries = (c.DataEntry?.Others ?? []).map((o) => o.Name).filter((n): n is string => !!n);
      return { type: "generic", name, notes: [notes, ...entries].filter(Boolean).join("\n") || undefined, fields: [] };
    }
    case 18: // login-only entry (credentials live in the CSV / Description), no host block
    case 26: // RDM credential entry (Pleasant Password Server) — credentials come from the CSV merge
      return { type: "login", name, notes, fields: [] };
    default:
      return {
        type: "generic",
        name,
        notes: [notes, `RDM ConnectionType ${c.ConnectionType ?? "?"}`].filter(Boolean).join("\n"),
        fields: [],
      };
  }
}

/** Parse an RDM JSON export. Throws if it isn't a recognizable RDM database. */
export function parseRdm(text: string): RdmParsed {
  const db = JSON.parse(text) as { Connections?: RdmConn[]; DatabaseID?: string };
  if (!Array.isArray(db.Connections) || !db.DatabaseID) {
    throw new Error("Not an RDM export (missing Connections/DatabaseID)");
  }

  const nodes = new Map<string, RdmNode>();
  // Ensure a folder node exists for `path` and every ancestor; returns nothing.
  const ensureFolder = (path: string) => {
    const segs = segments(path);
    for (let i = 1; i <= segs.length; i++) {
      const p = segs.slice(0, i).join("\\");
      if (!nodes.has(p)) nodes.set(p, { path: p, name: segs[i - 1], parentPath: parentOf(p), kind: "folder" });
    }
  };

  const accesses: RdmAccess[] = [];
  for (const c of db.Connections) {
    const group = c.Group || "";
    if (!group) continue;
    if (c.ConnectionType === 25) {
      // A group entry: its Group is its own full path. IP ⇒ it represents a device.
      ensureFolder(group);
      const node = nodes.get(group)!;
      if (c.GroupDetails?.IP) {
        node.kind = "device";
        node.meta = { category: inferCategory(c.Name || node.name), address: cleanAddr(c.GroupDetails.IP) };
      }
    } else {
      // A leaf connection: it lives inside its Group path.
      ensureFolder(group);
      const access = leafToAccess(c);
      if (access) accesses.push({ ...access, groupPath: group, id: c.ID });
    }
  }

  // Collapse to the backend depth limit + tally counts (shared with the mRemoteNG importer).
  return buildImportTree([...nodes.values()], accesses);
}

// --- Credential merge from the RDM CSV export (passwords are plaintext there) ---

/** A credential row from the RDM CSV export. */
export interface RdmCsvCred {
  id?: string; // RDM connection ID, from the WebUrl `Session=` param (exact merge key)
  group?: string;
  name: string;
  username?: string;
  domain?: string;
  password?: string;
}

const sessionId = (webUrl: string): string | undefined => {
  const m = /Session=([0-9a-fA-F-]{36})/.exec(webUrl || "");
  return m ? m[1].toLowerCase() : undefined;
};

/** Parse the RDM CSV export into credential rows. Reuses the shared CSV parser. */
export function parseRdmCsv(text: string): RdmCsvCred[] {
  const rows = parseCSV(text);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const idx = (...names: string[]) => header.findIndex((h) => names.includes(h));
  const ni = idx("name", "title");
  const gi = idx("group");
  const ui = idx("credentialusername", "username", "user");
  const di = idx("credentialdomain", "domain");
  const pi = idx("credentialpassword", "password", "pass");
  const wi = header.indexOf("weburl"); // the Session id lives only in WebUrl, not the plain URL
  const out: RdmCsvCred[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const name = (ni >= 0 ? row[ni] : row[0])?.trim() ?? "";
    if (!name) continue;
    out.push({
      id: wi >= 0 ? sessionId(row[wi]) : undefined,
      group: gi >= 0 ? row[gi]?.trim() : undefined,
      name,
      username: ui >= 0 ? row[ui]?.trim() || undefined : undefined,
      domain: di >= 0 ? row[di]?.trim() || undefined : undefined,
      password: pi >= 0 ? row[pi] || undefined : undefined,
    });
  }
  return out;
}

const norm = (s?: string): string => (s ?? "").trim().toLowerCase();

function setField(a: RdmAccess, key: string, label: string, type: Field["type"], value: string) {
  if (!value) return;
  const existing = a.fields.find((x) => x.key === key);
  if (existing) existing.value = value;
  else a.fields.push(fld(key, label, type, value));
}

/** Merge CSV credentials onto parsed accesses, by RDM ID (exact) then (group, name). Mutates
 *  `parsed.accesses` in place; returns how many got a credential. */
export function applyCsvCreds(parsed: RdmParsed, creds: RdmCsvCred[]): { matched: number; total: number } {
  const byId = new Map<string, RdmCsvCred>();
  const byKey = new Map<string, RdmCsvCred>();
  for (const c of creds) {
    if (c.id) byId.set(c.id, c);
    byKey.set(`${norm(c.group)} ${norm(c.name)}`, c);
  }
  let matched = 0;
  for (const a of parsed.accesses) {
    const c = (a.id ? byId.get(a.id) : undefined) ?? byKey.get(`${norm(a.groupPath)} ${norm(a.name)}`);
    if (!c) continue;
    matched++;
    if (c.domain && a.type === "rdp") {
      setField(a, "domain", "Domain", "text", c.domain);
      if (c.username) setField(a, "username", "Username", "username", c.username);
    } else if (c.username) {
      setField(a, "username", "Username", "username", c.domain ? `${c.domain}\\${c.username}` : c.username);
    }
    if (c.password) setField(a, "password", "Password", "password", c.password);
  }
  return { matched, total: parsed.accesses.length };
}
